/**
 * Hero Reorganization Utility
 * 
 * Handles hero layout changes with intelligent item reorganization.
 * 
 * ARCHITECTURE:
 * - Dry run (preview): Client-side matching for modal preview
 * - Actual execution: Single backend API call in atomic transaction
 * 
 * This eliminates the previous approach of 12-16+ sequential API calls
 * which caused database deadlocks and partial failures.
 * 
 * MIGRATION NOTE (Nov 2025):
 * This file was simplified from complex multi-call orchestration to a single
 * backend API call. The old batch functions (batchReorderItems, batchCreateItems,
 * batchDeleteItems) were removed as they caused deadlocks. Preview/dry-run logic
 * remains client-side for immediate UI feedback.
 */

import { apiFetch } from '../../core/utils/api';
import {
  parseLayoutValue,
  stringifyLayoutValue,
  validateLayoutArray,
  applyLayoutByMode,
} from './heroLayoutUtils';

// ============================================================================
// CSRF Token Utility
// ============================================================================

/**
 * Get CSRF token from cookies
 * @returns {string|null} CSRF token or null if not found
 */
function getCSRFToken() {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// ============================================================================
// Custom Error Types
// ============================================================================

class ReorganizationError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'ReorganizationError';
    this.details = details;
  }
}

class LayoutUpdateError extends ReorganizationError {
  constructor(message, details = {}) {
    super(message, details);
    this.name = 'LayoutUpdateError';
  }
}

class ReorderError extends ReorganizationError {
  constructor(message, details = {}) {
    super(message, details);
    this.name = 'ReorderError';
  }
}

class CreateError extends ReorganizationError {
  constructor(message, details = {}) {
    super(message, details);
    this.name = 'CreateError';
  }
}

// ============================================================================
// Logging Utilities
// ============================================================================

const isDev = process.env.NODE_ENV === 'development';

const isHeroDebugEnabled = (() => {
  const raw = (process.env.REACT_APP_HERO_DEBUG || "").trim().toLowerCase();
  if (!raw) return false;
  return raw === "1" || raw === "true" || raw === "yes" || raw === "enabled";
})();

const shouldLogInfo = isDev || isHeroDebugEnabled;

function logInfo(message, ...args) {
  // Avoid noisy logs in production/tests; allow opt-in via REACT_APP_HERO_DEBUG.
  if (!shouldLogInfo) return;
  console.log(`[HERO_DEBUG] ${message}`, ...args);
}

function logError(message, ...args) {
  console.error(`[HERO_DEBUG] [ERROR] ${message}`, ...args);
}

function logWarn(message, ...args) {
  if (shouldLogInfo) {
    console.warn(`[HERO_DEBUG] [WARN] ${message}`, ...args);
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Groups items by their type
 * 
 * @param {Array} children - Array of hero children items
 * @returns {Object} Object with type keys and ID arrays as values
 */
function groupItemsByType(children) {
  logInfo('groupItemsByType called with', children ? children.length : 0, 'items');
  const grouped = {
    image: [],
    title: [],
    text: [],
    button_collection: [],
    video: [],
    file: [],
    button: [],
  };

  if (!Array.isArray(children)) {
    logWarn('groupItemsByType: children is not an array', children);
    return grouped;
  }

  // Sort by existing order first
  const sorted = (Array.isArray(children) ? children.filter(Boolean) : []).slice().sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

  // Group by type
  sorted.forEach((item) => {
    const type = item.type;
    if (grouped.hasOwnProperty(type)) {
      grouped[type].push(item);
    } else {
      logWarn(`groupItemsByType: Unknown item type "${type}"`, item);
    }
  });

  logInfo('Grouped items by type:', grouped);
  return grouped;
}

/**
 * Matches items to column sequences, respecting column boundaries.
 * This is column-aware: items are assigned to specific columns based on the preset structure.
 * 
 * @param {Array<Array<string>>} columnSequences - 2D array where each inner array is a column's item types
 *   Example: [['image'], ['title', 'text', 'button_collection']]
 * @param {Object} groupedItems - Items grouped by type
 * @param {string} orphanStrategy - How to handle orphans ('keep' or 'delete')
 * @param {Object} orphanDecisions - Map of item ID -> boolean (true = keep, false = delete)
 * @returns {Object} { matched: Array, creates: Array, orphans: Array }
 */
function matchItemsToSequence(columnSequences, groupedItems, orphanStrategy = 'keep', orphanDecisions = {}) {
  logInfo('matchItemsToSequence called');
  const matched = []; // Items that will be reordered
  const creates = []; // Item types that need to be created
  const used = new Set(); // Track which item IDs have been assigned

  if (!Array.isArray(columnSequences)) {
    logWarn('matchItemsToSequence: columnSequences is not an array', columnSequences);
    return { matched: [], creates: [], orphans: [] };
  }

  let globalOrder = 0;

  // Process each column
  for (let colIdx = 0; colIdx < columnSequences.length; colIdx++) {
    const itemTypes = columnSequences[colIdx];

    if (!Array.isArray(itemTypes)) {
      logWarn(`matchItemsToSequence: Column ${colIdx} is not an array`, itemTypes);
      continue;
    }

    // Process each item type within the column
    for (let itemIdx = 0; itemIdx < itemTypes.length; itemIdx++) {
      const itemType = itemTypes[itemIdx];
      const availableItems = groupedItems[itemType] || [];

      // Find the first unused item of this type
      const item = availableItems.find((i) => !used.has(i.id));

      if (item) {
        // We have an item of this type available
        matched.push({
          item,
          newOrder: globalOrder,
          type: itemType,
          columnIndex: colIdx,
        });
        used.add(item.id);
      } else {
        // Need to create a new item of this type
        creates.push({
          type: itemType,
          order: globalOrder,
          columnIndex: colIdx,
        });
      }

      globalOrder++;
    }
  }

  // Find orphaned items (items not assigned to any column)
  const orphans = [];
  const orphansToKeep = [];
  const orphansToDelete = [];
  
  Object.keys(groupedItems).forEach((type) => {
    const items = groupedItems[type];
    items.forEach((item) => {
      if (!used.has(item.id)) {
        orphans.push(item);
        // Check individual decision for this orphan
        const shouldKeep = orphanDecisions[item.id] !== false; // Default to keep if not specified
        if (shouldKeep) {
          orphansToKeep.push(item);
        } else {
          orphansToDelete.push(item);
        }
      }
    });
  });

  // Handle orphans based on strategy and individual decisions
  if (orphanStrategy === 'keep' && orphansToKeep.length > 0) {
    // Place kept orphans at the end, in the last column
    orphansToKeep.forEach((item) => {
      matched.push({
        item,
        newOrder: globalOrder,
        type: item.type,
        columnIndex: columnSequences.length - 1, // Last column
      });
      globalOrder++;
    });
  }

  logInfo('Column-aware match result:', {
    matched: matched.length,
    creates: creates.length,
    orphansToKeep: orphansToKeep.length,
    orphansToDelete: orphansToDelete.length,
    totalOrphans: orphans.length,
    columns: columnSequences.length,
  });

  return { 
    matched, 
    creates, 
    orphans: orphanStrategy === 'keep' ? orphans : orphans, // Return all orphans for preview
    orphansToDelete: orphansToDelete  // Track which ones to delete
  };
}

/**
 * Builds reorder operations from match result
 * 
 * @param {Object} matchResult - Result from matchItemsToSequence
 * @param {Array} currentChildren - Current hero children
 * @returns {Array} Array of reorder operations
 */
function buildReorderOperations(matchResult, currentChildren) {
  const operations = [];

  matchResult.matched.forEach(({ item, newOrder }) => {
    const currentOrder = item.order ?? 0;
    
    // Only reorder if order has changed (optimization)
    if (currentOrder !== newOrder) {
      operations.push({
        itemId: item.id,
        itemType: item.type,
        currentOrder,
        newOrder,
        version: item.version || 1, // Include item version for optimistic locking
      });
    }
  });

  // Sort by itemId for consistent API call order
  operations.sort((a, b) => a.itemId - b.itemId);

  logInfo(`Built ${operations.length} reorder operations`);
  return operations;
}

/**
 * Builds create operations from match result
 * 
 * @param {Object} matchResult - Result from matchItemsToSequence
 * @param {number} componentId - Parent component ID
 * @param {number} heroId - Parent hero ID
 * @returns {Array} Array of create operations
 */
function buildCreateOperations(matchResult, componentId, heroId) {
  const operations = matchResult.creates.map((createSpec) => ({
    type: createSpec.type,
    order: createSpec.order,
    parent_hero_id: heroId,
    component_id: componentId,
  }));

  logInfo(`Built ${operations.length} create operations`);
  return operations;
}

// ============================================================================
// API Call Functions
// ============================================================================
// NOTE: Most API call functions have been removed as the backend now handles
// reorganization in a single atomic transaction. Only updateHeroLayoutOnly
// remains for custom layouts without reorganization.

/**
 * Updates only the hero layout without any item reorganization.
 * Used for custom layouts where user manually defines the grid but doesn't want items moved.
 * 
 * @param {number} heroId - Hero ID
 * @param {string} newLayout - New layout value (JSON string)
 * @param {string} mode - Layout mode ('one', 'two', 'all')
 * @param {string} activeBP - Active breakpoint
 * @param {Object} currentValue - Current hero_layout value
 * @param {Function} onProgress - Optional progress callback
 * @param {number} heroVersion - Current hero version for optimistic locking
 * @returns {Promise<Object>} { success: boolean, data?: Object, error?: string }
 */
async function updateHeroLayoutOnly(heroId, newLayout, mode, activeBP, currentValue, onProgress, heroVersion = 1) {
  logInfo(`Updating hero layout only (no reorganization) for hero ${heroId}, version: ${heroVersion}`);

  try {
    onProgress?.('validating', 5);

    // Apply the layout update based on mode
    const updatedLayout = applyLayoutByMode(currentValue, newLayout, mode, activeBP);

    onProgress?.('updating_layout', 50);

    // Update hero via API
    const payload = {
      hero_layout: updatedLayout,
      version: heroVersion,
    };
    
    logInfo(`Sending PATCH (layout only) with payload:`, payload);
    
    const response = await apiFetch(`/api/items/hero/${heroId}/update/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }
    
    const data = await response.json();

    onProgress?.('complete', 100);

    logInfo('Hero layout updated successfully (no reorganization)');
    return { success: true, data: data };
  } catch (error) {
    logError('Failed to update hero layout:', error);
    return {
      success: false,
      error: error.message || 'Failed to update hero layout',
    };
  }
}

// ============================================================================
// Main Reorganization Function
// ============================================================================

/**
 * Main reorganization function.
 * 
 * ARCHITECTURE:
 * - Dry run (preview): Client-side matching for modal preview
 * - Actual execution: Single backend API call in atomic transaction
 * 
 * This eliminates the previous approach of 12-16+ sequential API calls
 * which caused database deadlocks and partial failures.
 * 
 * @param {number} heroId - ID of hero being edited
 * @param {number} componentId - ID of parent component
 * @param {string} newLayout - New layout value like "[3, 3]"
 * @param {Object|null} preset - Preset object with columnSequences (null for custom)
 * @param {Array} currentChildren - Current hero children
 * @param {string} mode - Current mode ('one'|'two'|'all')
 * @param {string} activeBP - Active breakpoint
 * @param {Object} currentValue - Current hero_layout value
 * @param {Object} options - Additional options
 * @param {boolean} options.dryRun - If true, returns preview without executing
 * @param {Object} options.orphanDecisions - Map of item ID -> keep (true) or delete (false)
 * @param {Function} options.onProgress - Progress callback
 * @param {number} options.heroVersion - Current hero version for optimistic locking
 * @returns {Promise<Object>} Result with success flag and data
 * 
 * IMPLEMENTATION:
 * - DryRun: Client-side matching for modal preview (fast)
 * - Execute: Single API call to /api/items/hero/{id}/reorganize/ (atomic)
 */
export async function reorganizeHeroItems(
  heroId,
  componentId,
  newLayout,
  preset,
  currentChildren,
  mode,
  activeBP,
  currentValue,
  options = {}
) {
  const { 
    dryRun = false, 
    onProgress, 
    heroVersion = 1, 
    orphanDecisions = {}, 
    preselectedOrphans = null 
  } = options;

  logInfo('reorganizeHeroItems called with:', {
    heroId, newLayout, mode, activeBP, dryRun, heroVersion
  });

  // ======== Validation ========
  if (!heroId || !componentId) {
    const errorMsg = 'Invalid heroId or componentId';
    logError(errorMsg, { heroId, componentId });
    return { success: false, error: errorMsg };
  }

  if (!newLayout || typeof newLayout !== 'string') {
    const errorMsg = 'Invalid layout value';
    logError(errorMsg, { newLayout });
    return { success: false, error: errorMsg };
  }

  const layoutArray = parseLayoutValue(newLayout);
  if (!layoutArray || !validateLayoutArray(layoutArray)) {
    const errorMsg = 'Invalid layout array';
    logError(errorMsg, { newLayout, layoutArray });
    return { success: false, error: errorMsg };
  }

  onProgress?.('validating', 5);

  // ======== Check if Reorganization is Needed ========
  // If no preset or no columnSequences, just update layout without reorganizing items
  if (!preset || !preset.columnSequences || !Array.isArray(preset.columnSequences)) {
    logInfo('No columnSequences found - updating layout only (no item reorganization)');
    return await updateHeroLayoutOnly(heroId, newLayout, mode, activeBP, currentValue, onProgress, heroVersion);
  }

  // ======== DRY RUN: Preview Mode ========
  if (dryRun) {
    logInfo('Dry run mode: returning planned operations without executing');
    
    onProgress?.('analyzing', 10);
    const groupedItems = groupItemsByType(currentChildren || []);
    
    onProgress?.('planning', 20);
    const matchResult = matchItemsToSequence(
      preset.columnSequences, 
      groupedItems, 
      'keep', 
      orphanDecisions
    );
    
    const reorderOps = buildReorderOperations(matchResult, currentChildren);
    const createOps = buildCreateOperations(matchResult, componentId, heroId);
    
    onProgress?.('prepared', 25);
    
    return {
      success: true,
      dryRun: true,
      operations: {
        reorders: reorderOps,
        creates: createOps,
        orphans: matchResult.orphans,
      },
    };
  }

  // ======== ACTUAL REORGANIZATION: Single API Call ========
  logInfo('Calling backend /reorganize endpoint', {
    heroId,
    newLayout,
    mode,
    orphanDecisions,
    orphanCount: Object.keys(orphanDecisions).length,
  });

  onProgress?.('updating_layout', 30);

  try {
    const response = await apiFetch(`/api/items/hero/${heroId}/reorganize/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        new_layout: newLayout,
        preset_id: preset?.id || null,
        column_sequences: preset?.columnSequences || [],
        orphan_decisions: orphanDecisions,
        mode: mode,
        active_bp: activeBP,
        version: heroVersion,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      // Handle specific error codes
      if (response.status === 412) {
        throw new Error('Version conflict');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();

    onProgress?.('complete', 100);

    logInfo('Hero reorganization completed successfully via backend');
    logInfo('Backend response:', {
      success: true,
      operations: data.operations_summary,
      newVersion: data.version,
    });

    return {
      success: true,
      data: data,
      operations_summary: data.operations_summary
    };

  } catch (error) {
    logError('Hero reorganization failed:', error);
    return {
      success: false,
      error: error.message || 'Failed to reorganize hero items',
    };
  }
}

// ============================================================================
// LEGACY FUNCTION (Kept for reference during migration - Nov 2025)
// ============================================================================
// The old reorganizeHeroItems function orchestrated 12-16+ API calls sequentially:
// 1. updateHeroLayout
// 2. batchReorderItems (sequential reorder calls)
// 3. batchCreateItems (sequential create calls)
// 4. batchDeleteItems (sequential delete calls)
// 5. handleOrphans (more reorder calls)
// 6. refreshHeroData
//
// This caused database deadlocks and partial failures. The new implementation
// uses a single backend endpoint that handles everything in one transaction.
// ============================================================================

// ============================================================================
// Exports
// ============================================================================

export {
  // Main function
  reorganizeHeroItems as default,
  
  // Helper functions (for testing and dry run preview)
  groupItemsByType,
  matchItemsToSequence,
  buildReorderOperations,
  buildCreateOperations,
  
  // API functions
  updateHeroLayoutOnly,
  
  // Error types (kept for compatibility)
  ReorganizationError,
  LayoutUpdateError,
  ReorderError,
  CreateError,
};
